import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lockup } from "@/components/Brand";
import api, { errMsg } from "@/lib/api";

const Shell = ({ children, testid }) => (
  <div className="min-h-screen pp-grid-bg flex items-center justify-center p-6">
    <div className="w-full max-w-md pp-card p-8 shadow-xl" data-testid={testid}>
      <Link to="/" className="inline-block text-foreground mb-6"><Lockup height={20} bar="var(--brand-teal)" /></Link>
      {children}
    </div>
  </div>
);

export function PasswordRules({ value }) {
  return <div data-testid="password-rules" className="text-[11px] text-muted-foreground mt-1.5">At least <span className={value.length >= 10 ? "text-success-600 font-bold" : "font-bold"}>10 characters</span>, and not a common password like “password123”.</div>;
}

// Request a reset link. The confirmation never says whether the account exists — it mirrors the backend's non-disclosure.
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => { e.preventDefault(); setBusy(true); setErr(""); try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); } };
  return (
    <Shell testid="forgot-page">
      {sent ? (
        <div data-testid="forgot-sent">
          <h1 className="font-head text-2xl font-extrabold tracking-tight">Check your email</h1>
          <p className="text-sm text-subtle mt-2">If an account exists for <span className="font-semibold text-foreground">{email}</span>, a reset link is on its way. It lasts 60 minutes and works once.</p>
          <p className="text-sm text-subtle mt-2">Nothing arrived after a few minutes? Check your spam folder, make sure the address is the one you signed up with, then <button type="button" onClick={() => setSent(false)} className="font-bold underline">try again</button>.</p>
          <Link to="/login" data-testid="forgot-back" className="pp-btn-ghost w-full mt-6 justify-center">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-form">
          <h1 className="font-head text-2xl font-extrabold tracking-tight">Forgotten your password?</h1>
          <p className="text-sm text-muted-foreground mt-1">Enter the email you sign in with and we'll send a link to choose a new one.</p>
          <div className="mt-6"><label className="pp-label" htmlFor="forgot-email">Email</label><input id="forgot-email" data-testid="forgot-email" type="email" autoComplete="email" required className="pp-input" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          {err && <div role="alert" data-testid="forgot-error" className="mt-3 text-sm text-danger-800">{err}</div>}
          <button data-testid="forgot-submit" disabled={busy} className="pp-btn-dark w-full mt-6">{busy ? "Sending…" : "Send reset link"}</button>
          <Link to="/login" className="block text-center text-sm font-bold text-muted-foreground hover:text-foreground mt-4">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

const REASON_TITLE = { expired: "This link has expired", used: "This link has already been used", invalid: "This link isn't valid" };

// Complete a reset from the emailed link. Expired / used / invalid are told apart; a fresh link can be requested from here.
export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [status, setStatus] = useState(null);   // {valid, reason}
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!token) { setStatus({ valid: false, reason: "invalid" }); return; } api.get(`/auth/reset-password/${encodeURIComponent(token)}`).then((r) => setStatus(r.data)).catch(() => setStatus({ valid: false, reason: "invalid" })); }, [token]);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw !== pw2) { setErr("The two passwords don't match."); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw }); nav("/login?reset=done", { replace: true }); }
    catch (ex) { const d = ex?.response?.data?.detail; if (d && typeof d === "object" && d.reason && d.reason !== "weak") setStatus({ valid: false, reason: d.reason }); else setErr((d && d.message) || errMsg(ex)); }
    finally { setBusy(false); }
  };
  if (!status) return <Shell testid="reset-page"><p className="text-sm text-muted-foreground">Checking your link…</p></Shell>;
  if (!status.valid) return (
    <Shell testid="reset-page">
      <div data-testid={`reset-${status.reason}`}>
        <h1 className="font-head text-2xl font-extrabold tracking-tight">{REASON_TITLE[status.reason] || REASON_TITLE.invalid}</h1>
        <p className="text-sm text-subtle mt-2">{status.reason === "expired" ? "Reset links last 60 minutes. Request a new one and use it straight away." : status.reason === "used" ? "Each link works once. If you didn't complete a reset just now, request a new link and change your password immediately." : "The link may have been cut short by your email client. Request a new one below."}</p>
        <Link to="/forgot-password" data-testid="reset-request-new" className="pp-btn-dark w-full mt-6 justify-center">Request a new link</Link>
        <Link to="/login" className="block text-center text-sm font-bold text-muted-foreground hover:text-foreground mt-4">Back to sign in</Link>
      </div>
    </Shell>
  );
  return (
    <Shell testid="reset-page">
      <form onSubmit={submit} data-testid="reset-form">
        <h1 className="font-head text-2xl font-extrabold tracking-tight">Choose a new password</h1>
        <p className="text-sm text-muted-foreground mt-1">You'll be signed out everywhere else once it's changed.</p>
        <div className="mt-6"><label className="pp-label" htmlFor="reset-pw">New password</label><input id="reset-pw" data-testid="reset-password" type="password" autoComplete="new-password" required className="pp-input" value={pw} onChange={(e) => setPw(e.target.value)} /><PasswordRules value={pw} /></div>
        <div className="mt-4"><label className="pp-label" htmlFor="reset-pw2">Repeat new password</label><input id="reset-pw2" data-testid="reset-password2" type="password" autoComplete="new-password" required className="pp-input" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
        {err && <div role="alert" data-testid="reset-error" className="mt-3 text-sm text-danger-800">{err}</div>}
        <button data-testid="reset-submit" disabled={busy || pw.length < 10} className="pp-btn-dark w-full mt-6">{busy ? "Changing…" : "Change password"}</button>
      </form>
    </Shell>
  );
}
